import { SupportTicketIdZod } from "@redotech/effect-api-shared/ids/zod";
import { ConversationPlatform } from "@redotech/redo-model/conversation";
import {
  CsatSortableField,
  SatisfactionResponseStatusTypes,
} from "@redotech/redo-model/support/csat-response";
import { SortDirection } from "@redotech/redo-model/tables/table";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

export type CsatResponse = z.infer<typeof csatResponseSchema>;

export const csatResponseSchema = z.object({
  _id: zExt.objectId(),
  team: zExt.objectId(),
  // v1 rows carry a Mongo conversation ObjectId; v2 rows carry a branded
  // SupportTicketId instead. Exactly one is present.
  conversation: zExt.objectId().optional(),
  supportTicketId: SupportTicketIdZod.optional(),
  rating: z.number(),
  comment: z.string(),
  channel: createEnum(ConversationPlatform),
  status: createEnum(SatisfactionResponseStatusTypes),
  createdAt: z.date(),
  updatedAt: z.date(),
  sendAt: z.date().optional(),
});

export const getCsatResponsesForConversation = {
  name: "getCsatResponsesForConversation" as const,
  input: z.object({ conversationId: z.string() }),
  output: z.array(csatResponseSchema),
};

export type GetCsatResponsesForConversationInput = z.infer<
  typeof getCsatResponsesForConversation.input
>;
export type GetCsatResponsesForConversationOutput = z.infer<
  typeof getCsatResponsesForConversation.output
>;

const analyticsCsatResponseSchema = csatResponseSchema.extend({
  customer: z.any(),
  assignee: zExt.objectId().optional(),
});

const csatAnalyticsFilters = z.object({
  platforms: z.array(createEnum(ConversationPlatform)).optional(),
  dateRangeStart: z.date(),
  dateRangeEnd: z.date(),
});

export const getCsatResponsesForTeam = {
  name: "getCsatResponsesForTeam" as const,
  input: z.object({
    pageContinue: z.string().optional(),
    pageSize: z.number(),
    pageStop: z.string().optional(),
    sort: z.object({
      direction: createEnum(SortDirection),
      key: createEnum(CsatSortableField),
    }),
    filters: csatAnalyticsFilters,
  }),
  output: z.object({
    data: z.array(analyticsCsatResponseSchema),
    pageNext: z.string().optional(),
  }),
};

export type GetCsatResponsesForTeamInput = z.infer<
  typeof getCsatResponsesForTeam.input
>;
export type GetCsatResponsesForTeamOutput = z.infer<
  typeof getCsatResponsesForTeam.output
>;

export const getCsatResponsesForTeamCount = {
  name: "getCsatResponsesForTeamCount" as const,
  input: z.object({ filters: csatAnalyticsFilters }),
  output: z.object({ count: z.number() }),
};

export type GetCsatResponsesForTeamCountInput = z.infer<
  typeof getCsatResponsesForTeamCount.input
>;
